import React, { useEffect, useState } from "react";
import axios from "@/lib/axios";
import { Button } from "@/components/ui/button";
import { API_BASE_URL } from "@/config";
import { useLoader } from "@/contexts/LoaderContext";
import { toast } from "sonner";
import { FileUp, Database, AlertCircle, CheckCircle2 } from "lucide-react";
import JourneyHeader from "@/components/JourneyHeader";

const MappingTable = ({ title, modelColumns, csvColumns, mappings, onMappingChange }) => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-8">
      <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex items-center gap-2">
        <Database className="h-5 w-5 text-intellend-green" />
        <h2 className="text-lg font-bold text-gray-900 capitalize">
          {title.replace(/_/g, ' ')} Configuration
        </h2>
      </div>
      <div className="p-0 overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-gray-50/50">
              <th className="border-b border-r px-6 py-3 text-left font-semibold text-gray-600 w-1/2">Database Field</th>
              <th className="border-b px-6 py-3 text-left font-semibold text-gray-600 w-1/2">CSV Column Match</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {modelColumns.map((col) => (
              <tr key={col} className="hover:bg-gray-50/30 transition-colors">
                <td className="border-b border-r px-6 py-3 font-medium text-gray-700">{col}</td>
                <td className="border-b px-6 py-3">
                  {['id', 'anonymized_id', 'bap_fkey', 'bap_lead_detail', 'lender_details', 'created_at', 'updated_at', 'deleted_at', 'status', 'rejected_by_le', 'rejected_by_user', 'offer_selected_by_user'].includes(col) ? (
                    <span className="text-gray-400 italic text-sm">-- Auto Generated --</span>
                  ) : (
                    <select
                      value={mappings[col] || ""}
                      onChange={(e) => onMappingChange(col, e.target.value)}
                      className="w-full bg-transparent border-0 border-b border-gray-200 focus:ring-0 focus:border-intellend-green py-1"
                    >
                      <option value="">-- No Match --</option>
                      {csvColumns.map(cc => (
                        <option key={cc} value={cc}>{cc}</option>
                      ))}
                    </select>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ReferenceDataDisplay = () => {
  const [data, setData] = useState<{ baps: any[], lenders: any[] } | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/admin/reference-data/`);
        setData(res.data);
      } catch (err) {
        console.error("Failed to fetch reference data");
      }
    };
    if (isOpen && !data) fetchData();
  }, [isOpen, data]);

  return (
    <div className="mb-8 bg-white rounded-xl border border-blue-200 shadow-sm overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-6 py-4 bg-blue-50 text-blue-900 hover:bg-blue-100 transition-colors"
      >
        <div className="flex items-center gap-2 font-semibold">
          <AlertCircle className="h-5 w-5" />
          <span>Reference IDs (BAPs & Lenders)</span>
        </div>
        <span className="text-sm">{isOpen ? "Hide" : "Show"}</span>
      </button>

      {isOpen && data && (
        <div className="p-6 grid md:grid-cols-2 gap-8 bg-white border-t border-blue-100">
          <div>
            <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">BAPs</span>
            </h3>
            <div className="max-h-60 overflow-y-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left border-b">ID</th>
                    <th className="px-3 py-2 text-left border-b">Name</th>
                  </tr>
                </thead>
                <tbody>
                  {data.baps.map((item: any) => (
                    <tr key={item.id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="px-3 py-2 font-mono text-xs">{item.id}</td>
                      <td className="px-3 py-2">{item.name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div>
            <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">Lenders</span>
            </h3>
            <div className="max-h-60 overflow-y-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left border-b">ID</th>
                    <th className="px-3 py-2 text-left border-b">Name</th>
                  </tr>
                </thead>
                <tbody>
                  {data.lenders.map((item: any) => (
                    <tr key={item.id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="px-3 py-2 font-mono text-xs">{item.id}</td>
                      <td className="px-3 py-2">{item.name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const UploadLeads = () => {
  const { setLoading } = useLoader();
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [currentSeparator, setCurrentSeparator] = useState("|");
  const [tableConfigs, setTableConfigs] = useState<Record<string, string[]>>({});
  const [csvColumns, setCsvColumns] = useState<string[]>([]);
  const [scrutinyResults, setScrutinyResults] = useState<any>(null);

  // Mappings: table_name -> { model_col -> csv_col }
  const [allMappings, setAllMappings] = useState<Record<string, Record<string, string>>>({});

  useEffect(() => {
    const loadConfigs = async () => {
      setLoading(true);
      try {
        const res = await axios.post(`${API_BASE_URL}/api/get_model_column_names/multi/`);
        setTableConfigs(res.data || {});

        const initialMappings: Record<string, Record<string, string>> = {};
        Object.keys(res.data).forEach(table => {
          initialMappings[table] = {};
        });
        setAllMappings(initialMappings);
      } finally {
        setLoading(false);
      }
    };
    loadConfigs();
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const text = reader.result as string;
      const firstLine = text.split(/\r?\n/)[0];
      const cols = firstLine.split(currentSeparator).map((c) => c.trim());

      // VALIDATION: Check for duplicate headers
      const normalized = cols.map(c => c.toLowerCase().trim());
      const seen = new Set();
      const duplicates = normalized.filter(c => {
        if (seen.has(c)) return true;
        seen.add(c);
        return false;
      });

      if (duplicates.length > 0) {
        toast.error(`Error: Multiple columns with the same name found: ${duplicates.join(", ")}`);
        e.target.value = ""; // Reset input
        return;
      }

      setCsvColumns(cols);
      setUploadedFile(file);

      // Auto-map across all tables
      const newAllMappings = JSON.parse(JSON.stringify(allMappings));
      Object.entries(tableConfigs).forEach(([tableName, modelCols]) => {
        modelCols.forEach((mcol) => {
          if (['id', 'anonymized_id', 'bap_fkey', 'bap_lead_detail', 'lender_details', 'created_at', 'updated_at', 'deleted_at', 'status', 'rejected_by_le', 'rejected_by_user', 'offer_selected_by_user'].includes(mcol)) return; // Skip restricted fields
          const match = cols.find(
            (c) => c.toLowerCase().trim() === mcol.toLowerCase().trim()
          );
          newAllMappings[tableName][mcol] = match || "";
        });
      });
      setAllMappings(newAllMappings);
      setScrutinyResults(null); // Reset scrutiny on new file
      toast.success("File uploaded! Columns have been auto-matched.");
    };
    reader.readAsText(file);
  };

  const handleMappingUpdate = (table: string, col: string, val: string) => {
    setAllMappings(prev => ({
      ...prev,
      [table]: {
        ...prev[table],
        [col]: val
      }
    }));
  };

  const handleScrutiny = async () => {
    if (!uploadedFile) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadedFile);
      formData.append("mappings", JSON.stringify(allMappings));
      formData.append("separator", currentSeparator);

      const res = await axios.post(`${API_BASE_URL}/api/scrutinize_multi_table_leads/`, formData);
      setScrutinyResults(res.data);
      if (res.data.issues && res.data.issues.length > 0) {
        toast.error("Scrutiny found issues.");
      } else {
        toast.success("Scrutiny check passed!");
      }
    } catch (err) {
      console.error("Scrutiny failed", err);
      toast.error("Scrutiny check failed.");
    } finally {
      setLoading(false);
    }
  };

  // Stats state
  const [uploadStats, setUploadStats] = useState<{
    rows_added: number;
    skipped_scores: number;
    failed_rows: { row: number; error: string }[];
    processed_rows?: any[];
    headers?: string[];
  } | null>(null);

  const handleSubmit = async () => {
    if (!uploadedFile) {
      toast.error("Please upload a file first");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadedFile);
      formData.append("mappings", JSON.stringify(allMappings));
      formData.append("separator", currentSeparator);

      const res = await axios.post(`${API_BASE_URL}/api/submit_multi_table_leads/`, formData);
      setUploadStats(res.data);
      toast.success("Upload processed successfully!");

    } catch (err) {
      console.error("Submit failed:", err);
      toast.error("Process failed. Please check your mapping and file format.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setUploadStats(null);
    setUploadedFile(null);
    setCsvColumns([]);
    setScrutinyResults(null);
    // Reset mappings as well if needed, or keep for convenience?
    // Keeping mappings for convenience as user might upload similar file.
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <JourneyHeader />

      <main className="flex-1 max-w-6xl mx-auto w-full p-8">

        {uploadStats ? (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 animate-in fade-in zoom-in duration-300">
            <div className="text-center mb-8">
              <div className="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle2 className="h-10 w-10 text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Upload Complete</h2>
              <p className="text-gray-500 mt-2">Here is the summary of your upload processing.</p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {/* Success Card */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 flex flex-col items-center">
                <span className="text-green-800 font-semibold mb-2">Rows Added</span>
                <span className="text-4xl font-bold text-green-700">{uploadStats.rows_added}</span>
              </div>

              {/* Skipped Card */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 flex flex-col items-center">
                <span className="text-blue-800 font-semibold mb-2">Skipped Scores</span>
                <span className="text-4xl font-bold text-blue-700">{uploadStats.skipped_scores}</span>
                <span className="text-xs text-blue-600 mt-2 text-center">(ScoreEngineResponse mapping not provided?)</span>
              </div>

              {/* Failed Card */}
              <div className={`border rounded-xl p-6 flex flex-col items-center ${uploadStats.failed_rows.length > 0 ? "bg-red-50 border-red-200" : "bg-gray-50 border-gray-200"}`}>
                <span className={`${uploadStats.failed_rows.length > 0 ? "text-red-800" : "text-gray-600"} font-semibold mb-2`}>Failed Rows</span>
                <span className={`text-4xl font-bold ${uploadStats.failed_rows.length > 0 ? "text-red-700" : "text-gray-700"}`}>{uploadStats.failed_rows.length}</span>
              </div>
            </div>

            {/* Failure Details */}
            {uploadStats.failed_rows.length > 0 && (
              <div className="mb-8">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                  Failure Details
                </h3>
                <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <tr className="bg-gray-50 sticky top-0">
                          <th className="px-4 py-2 text-left border-b font-semibold text-gray-600">CSV Row Index</th>
                          <th className="px-4 py-2 text-left border-b font-semibold text-gray-600">Error Message</th>
                        </tr>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {uploadStats.failed_rows.map((fail, idx) => (
                        <tr key={idx} className="hover:bg-red-50/30">
                          <td className="px-4 py-2 font-mono text-gray-700">{fail.row}</td>
                          <td className="px-4 py-2 text-red-600">{fail.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex justify-center gap-4">
              <Button
                onClick={handleReset}
                size="lg"
                className="bg-gray-900 hover:bg-gray-800 text-white px-8"
              >
                Upload Another File
              </Button>
              <Button
                onClick={() => {
                  if (!uploadStats.processed_rows || uploadStats.processed_rows.length === 0) {
                    toast.error("No processed data to download.");
                    return;
                  }

                  const headers = ["anon_id", "access_link", ...uploadStats.headers];
                  const csvRows = [headers.join(",")];

                  uploadStats.processed_rows.forEach((row: any) => {
                    const access_link = row.anon_id ? `${window.location.origin}/home/${row.anon_id}` : "";
                    const values = [
                      row.anon_id || "",
                      access_link,
                      ...uploadStats.headers.map((header: string) => {
                        const cell = row[header] === null || row[header] === undefined ? "" : row[header];
                        return `"${String(cell).replace(/"/g, '""')}"`; // Escape quotes
                      })
                    ];
                    csvRows.push(values.join(","));
                  });

                  const csvContent = csvRows.join("\n");
                  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.setAttribute("href", url);
                  link.setAttribute("download", `processed_leads_${new Date().toISOString().split('T')[0]}.csv`);
                  link.style.visibility = "hidden";
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                size="lg"
                className="bg-intellend-green hover:bg-intellend-green/90 text-white px-8"
              >
                Download Result CSV
              </Button>
            </div>
          </div>
        ) : (
          <>
            <ReferenceDataDisplay />

            {/* Step 1: Upload Details */}
            <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 mb-10">
              <div className="flex items-center gap-2 mb-6">
                <FileUp className="h-6 w-6 text-intellend-green" />
                <h2 className="text-xl font-bold text-gray-900">Step 1: Upload Source File</h2>
              </div>

              <div className="grid gap-8 md:grid-cols-3 items-end">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-600 uppercase tracking-wider text-[10px]">CSV Separator</label>
                  <input
                    type="text"
                    value={currentSeparator}
                    onChange={(e) => setCurrentSeparator(e.target.value)}
                    maxLength={1}
                    className="w-16 px-3 py-2 border border-gray-400 rounded-none focus:ring-0 focus:border-intellend-green text-center font-mono text-lg block"
                  />
                </div>
                <div className="md:col-span-2 space-y-2">
                  <label className="text-sm font-semibold text-gray-600">Select CSV File</label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-intellend-green/10 file:text-intellend-green hover:file:bg-intellend-green/20"
                  />
                </div>
              </div>

              {uploadedFile && (
                <div className="mt-6 space-y-3">
                  <div className="p-4 bg-green-50 text-green-700 rounded-lg flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5" />
                    <span className="font-medium">Selected File: {uploadedFile.name} ({csvColumns.length} columns detected)</span>
                  </div>

                  {csvColumns.length > 0 && (
                    <div className="p-4 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg">
                      <div className="flex items-center gap-2 mb-2 font-bold text-sm">
                        <AlertCircle className="h-4 w-4" />
                        <span>{csvColumns.filter(c => !Object.values(allMappings).some(tm => Object.values(tm).includes(c))).length} Unmatched Columns</span>
                      </div>
                      <div className="text-xs flex flex-wrap gap-2 text-amber-700/80 italic">
                        {csvColumns.filter(c => !Object.values(allMappings).some(tm => Object.values(tm).includes(c))).map(c => (
                          <span key={c} className="bg-amber-100 px-2 py-0.5 rounded border border-amber-200/50">{c}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </section>

            {/* Step 2: Mapping Sections */}
            {uploadedFile && (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="flex items-center gap-2 mb-6">
                  <Database className="h-6 w-6 text-intellend-green" />
                  <h2 className="text-xl font-bold text-gray-900">Step 2: Map Database Columns</h2>
                </div>

                {Object.entries(tableConfigs).map(([tableName, modelColumns]) => (
                  <MappingTable
                    key={tableName}
                    title={tableName}
                    modelColumns={modelColumns}
                    csvColumns={csvColumns}
                    mappings={allMappings[tableName]}
                    onMappingChange={(col, val) => handleMappingUpdate(tableName, col, val)}
                  />
                ))}

                {/* Step 3: Scrutiny */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 mb-10">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm">3</div>
                    <h2 className="text-xl font-bold text-gray-900">Step 3: Scrutiny Check</h2>
                  </div>

                  {!scrutinyResults ? (
                    <div className="text-center py-10">
                      <p className="text-gray-500 mb-6">Run a scrutiny check to validate the file content before uploading.</p>
                      <Button
                        onClick={handleScrutiny}
                        size="lg"
                        className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-8"
                      >
                        RUN SCRUTINY CHECK
                      </Button>
                    </div>
                  ) : (
                    <div>
                      {scrutinyResults.status === 'success' && scrutinyResults.issues.length === 0 ? (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                          <div className="flex items-center gap-2 text-green-800 font-semibold mb-2">
                            <CheckCircle2 className="h-5 w-5" />
                            <span>Scrutiny Passed!</span>
                          </div>
                          <p className="text-green-700 text-sm mb-3">
                            {scrutinyResults.message} ({scrutinyResults.row_count} rows ready to process)
                          </p>
                          <div className="text-sm text-green-700 space-y-1">
                            <div>• <span className="font-semibold">BAPLeadDetail:</span> {scrutinyResults.bap_lead_detail_count} records</div>
                            <div>• <span className="font-semibold">BusinessToPersonMapping:</span> {scrutinyResults.business_to_person_mapping_count} records</div>
                            <div>• <span className="font-semibold">ScoreEngineResponse:</span> {scrutinyResults.score_engine_response_count} records</div>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                          <div className="flex items-center gap-2 text-red-800 font-semibold mb-2">
                            <AlertCircle className="h-5 w-5" />
                            <span>Scrutiny Issues Found</span>
                          </div>
                          <ul className="list-disc list-inside text-red-700 text-sm">
                            {scrutinyResults.issues.map((issue: any, idx: number) => (
                              <li key={idx}>{issue}</li>
                            ))}
                          </ul>
                          <div className="mt-4">
                            <Button
                              onClick={handleScrutiny}
                              variant="outline"
                              className="border-red-200 text-red-700 hover:bg-red-50"
                            >
                              Retry Scrutiny
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Step 4: Final Upload */}
                {scrutinyResults && scrutinyResults.status === 'success' && scrutinyResults.issues.length === 0 && (
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 animate-in fade-in slide-in-from-bottom-2">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="h-8 w-8 rounded-full bg-intellend-green/10 flex items-center justify-center text-intellend-green font-bold text-sm">4</div>
                      <h2 className="text-xl font-bold text-gray-900">Step 4: Final Upload</h2>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="mb-6 text-center max-w-lg">
                        <p className="text-gray-600 mb-4">
                          The file has been scrutinized and is ready for import. This action will create:
                        </p>
                        <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm text-gray-700">
                          <div>• <span className="font-semibold">{scrutinyResults.bap_lead_detail_count}</span> BAPLeadDetail records</div>
                          <div>• <span className="font-semibold">{scrutinyResults.business_to_person_mapping_count}</span> BusinessToPersonMapping records</div>
                          <div>• <span className="font-semibold">{scrutinyResults.score_engine_response_count}</span> ScoreEngineResponse records</div>
                        </div>
                      </div>
                      <Button
                        onClick={handleSubmit}
                        size="lg"
                        className="bg-intellend-green hover:bg-intellend-green/90 px-16 h-14 text-xl font-black shadow-xl"
                      >
                        CONFIRM & UPLOAD
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {!uploadedFile && (
              <div className="py-20 flex flex-col items-center justify-center border-2 border-dashed border-gray-200 rounded-2xl bg-gray-50">
                <AlertCircle className="h-10 w-10 text-gray-400 mb-4" />
                <p className="text-gray-500 font-medium text-lg">Please upload a source file to begin mapping columns</p>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default UploadLeads;
